import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const WorkOrderDetailModal = ({ order, onClose, statusColors, qaStatuses }) => {
  const [currentOrder, setCurrentOrder] = useState({ ...order });
  const [saving, setSaving] = useState(false);
  const [uploadingWO, setUploadingWO] = useState(false);
  const [uploadingInv, setUploadingInv] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const woFileRef = useRef(null);
  const invFileRef = useRef(null);

  const handleFieldChange = (field, value) => {
    setCurrentOrder(prev => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const { error: updateError } = await supabase?.from('work_orders')?.update({
          work_order_number: currentOrder?.work_order_number,
          description: currentOrder?.description,
          location: currentOrder?.location,
          assigned_to: currentOrder?.assigned_to,
          work_type: currentOrder?.work_type,
          priority: currentOrder?.priority,
          amount: currentOrder?.amount ? parseFloat(currentOrder?.amount) : null,
          qa_status: currentOrder?.qa_status,
          notes: currentOrder?.notes,
        })?.eq('id', currentOrder?.id);

      if (updateError) throw updateError;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadPDF = async (file, type) => {
    const setUploading = type === 'work_order' ? setUploadingWO : setUploadingInv;
    const fieldKey = type === 'work_order' ? 'work_order_pdf_path' : 'invoice_pdf_path';

    if (!file || file?.type !== 'application/pdf') {
      setError('Please select a valid PDF file.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const filePath = `${currentOrder?.id}/${type}_${Date.now()}.pdf`;

      // Remove old file if exists
      const oldPath = currentOrder?.[fieldKey];
      if (oldPath) {
        await supabase?.storage?.from('work-order-pdfs')?.remove([oldPath]);
      }

      const { error: uploadError } = await supabase?.storage?.from('work-order-pdfs')?.upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase?.from('work_orders')?.update({ [fieldKey]: filePath })?.eq('id', currentOrder?.id);

      if (updateError) throw updateError;

      setCurrentOrder(prev => ({ ...prev, [fieldKey]: filePath }));
    } catch (err) {
      setError('Upload failed: ' + err?.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadPDF = async (filePath, filename) => {
    try {
      setError(null);
      const { data, error: signError } = await supabase?.storage?.from('work-order-pdfs')?.createSignedUrl(filePath, 3600);

      if (signError) throw signError;

      const link = document.createElement('a');
      link.href = data?.signedUrl;
      link.download = filename;
      link.target = '_blank';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (err) {
      setError('Download failed: ' + err?.message);
    }
  };

  const handlePrintPDF = async (filePath) => {
    try {
      setError(null);
      const { data, error: signError } = await supabase?.storage?.from('work-order-pdfs')?.createSignedUrl(filePath, 3600);

      if (signError) throw signError;

      const win = window.open(data?.signedUrl, '_blank');
      if (win) {
        win.onload = () => {
          setTimeout(() => win?.print(), 500);
        };
      }
    } catch (err) {
      setError('Print failed: ' + err?.message);
    }
  };

  const handleRemovePDF = async (type) => {
    const fieldKey = type === 'work_order' ? 'work_order_pdf_path' : 'invoice_pdf_path';
    const filePath = currentOrder?.[fieldKey];
    if (!filePath) return;
    if (!window.confirm('Remove this PDF attachment?')) return;

    try {
      setError(null);
      await supabase?.storage?.from('work-order-pdfs')?.remove([filePath]);
      await supabase?.from('work_orders')?.update({ [fieldKey]: null })?.eq('id', currentOrder?.id);
      setCurrentOrder(prev => ({ ...prev, [fieldKey]: null }));
    } catch (err) {
      setError('Remove failed: ' + err?.message);
    }
  };

  const PDFAttachmentSection = ({ type, label, icon, filePath, uploading, fileRef }) => (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon name={icon} size={16} className="text-primary" />
          <span className="text-sm font-semibold text-foreground">{label}</span>
        </div>
        {filePath && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
            <Icon name="CheckCircle" size={12} />
            Attached
          </span>
        )}
      </div>

      {filePath ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleDownloadPDF(filePath, `${currentOrder?.work_order_number}_${type}.pdf`)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium"
            >
              <Icon name="Download" size={14} />
              Download PDF
            </button>
            <button
              onClick={() => handlePrintPDF(filePath)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-muted text-foreground hover:bg-muted/80 transition-colors font-medium"
            >
              <Icon name="Printer" size={14} />
              Print
            </button>
            <button
              onClick={() => fileRef?.current?.click()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-muted text-foreground hover:bg-muted/80 transition-colors font-medium"
            >
              <Icon name="RefreshCw" size={14} />
              Replace
            </button>
            <button
              onClick={() => handleRemovePDF(type)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors font-medium"
            >
              <Icon name="Trash2" size={14} />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button
            onClick={() => fileRef?.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 w-full justify-center py-3 border-2 border-dashed border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all text-sm text-muted-foreground"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Icon name="Upload" size={16} />
                Click to attach PDF
              </>
            )}
          </button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={e => {
          const f = e?.target?.files?.[0];
          if (f) uploadPDF(f, type);
          e.target.value = '';
        }}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="ClipboardList" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-foreground">
                Work Order: <span className="font-mono">{currentOrder?.work_order_number}</span>
              </h2>
              <p className="text-xs text-muted-foreground">View, edit details, and manage PDF attachments</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-center gap-2">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}

          {saveSuccess && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
              <Icon name="CheckCircle" size={16} />
              Changes saved successfully
            </div>
          )}

          {/* Work Order Details */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Info" size={16} className="text-primary" />
              Work Order Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">WO Number *</label>
                <input
                  type="text"
                  value={currentOrder?.work_order_number || ''}
                  onChange={e => handleFieldChange('work_order_number', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">QA Status</label>
                <select
                  value={currentOrder?.qa_status || 'Pending Review'}
                  onChange={e => handleFieldChange('qa_status', e?.target?.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 font-medium ${statusColors?.[currentOrder?.qa_status] || 'bg-background text-foreground border-border'}`}
                >
                  {qaStatuses?.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                <input
                  type="text"
                  value={currentOrder?.description || ''}
                  onChange={e => handleFieldChange('description', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Location</label>
                <input
                  type="text"
                  value={currentOrder?.location || ''}
                  onChange={e => handleFieldChange('location', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Assigned To</label>
                <input
                  type="text"
                  value={currentOrder?.assigned_to || ''}
                  onChange={e => handleFieldChange('assigned_to', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Work Type</label>
                <input
                  type="text"
                  value={currentOrder?.work_type || ''}
                  onChange={e => handleFieldChange('work_type', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Priority</label>
                <select
                  value={currentOrder?.priority || 'Normal'}
                  onChange={e => handleFieldChange('priority', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {['Low', 'Normal', 'High', 'Urgent']?.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={currentOrder?.amount || ''}
                  onChange={e => handleFieldChange('amount', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="0.00"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
                <textarea
                  value={currentOrder?.notes || ''}
                  onChange={e => handleFieldChange('notes', e?.target?.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
            </div>
          </div>

          {/* PDF Attachments */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Paperclip" size={16} className="text-primary" />
              PDF Attachments
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PDFAttachmentSection
                type="work_order"
                label="Work Order PDF"
                icon="FileText"
                filePath={currentOrder?.work_order_pdf_path}
                uploading={uploadingWO}
                fileRef={woFileRef}
              />
              <PDFAttachmentSection
                type="invoice"
                label="Invoice PDF"
                icon="Receipt"
                filePath={currentOrder?.invoice_pdf_path}
                uploading={uploadingInv}
                fileRef={invFileRef}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="text-xs text-muted-foreground">
            Created: {currentOrder?.created_at ? new Date(currentOrder.created_at)?.toLocaleDateString() : '—'}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              variant="primary"
              iconName={saving ? undefined : 'Save'}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetailModal;
